// RecordDisplay.js - Component for displaying and editing fetched records
import React from "react";
import { Box, TextField, Button, CircularProgress, MenuItem, Select } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
// import { getClubLocation } from "../../../config/clubConfig";
import { getFolkLevelLocation } from "../../../config/folkLevelConfig";

export default function RecordDisplay({ 
    fetchedRecord, 
    editState, 
    editedRecord, 
    loading,
    onEdit, 
    onChange, 
    onUpdate,
    getDateStr,
    getTimeStr 
}) {
    if (!fetchedRecord) return null;

    const folkLevelOptions = [
        "FOLK Plus", "FOLK Graduate", "FOLK Enhanced", "FOLK Prelims", 
        "FOLK Foundation", "GFY Attended", "GFY Registered", "AOMC Attended"
    ];

    const ashrayaLevelOptions = [
        "Shraddavan", "Sevaka", "Sadhaka", "Vandana", "Upasaka", 
        "Charanashraya", "Prospective", "Completed", "Initiated", "Inactive"
    ];

    const courseMaterialOptions = [
        "Purchased", "Not Purchased", "Next Week", "No Money"
    ];

    const occupationOptions = [
        "Student", "Working", "Teaching Faculty", "Business", "Job Search", "Others"
    ];

    const folkCentreOptions = [
        "Alwal", "Ameerpet", "Banjara Hills", "BITS Hyderabad", "Chanda Nagar",
        "Dilsukhnagar", "Gachibowli", "Gandipet", "Himayath Nagar", "Hitech City",
        "Hyderabad", "IIT Hyderabad", "IITH Alumni", "Jagadgirigutta", "Kokapet/Gandipet",
        "Kondapur", "KPHB", "lingampalli", "Madhapur", "Manikonda", "Medchal",
        "Mehdipatnam", "Musheerabad", "Nanakramguda", "Narsingi", "NIT Warangal",
        "Not in Hyderabad", "Old City", "Other Place in Hyderabad", "Pragati Nagar", "SR Nagar"
    ];

    const renderField = (label, field, value, isEditable = true, isSelect = false, options = []) => (
        <div style={{ marginBottom: "4mm" }}>
            <span style={{ fontWeight: "500", marginRight: "4px" }}>{label}:</span>
            {editState[field] && isEditable ? (
                isSelect ? (
                    <Select
                        name={field}
                        value={editedRecord[field] || value || ""}
                        onChange={onChange}
                        variant="outlined"
                        size="small"
                        style={{ marginLeft: "10px" }}
                    >
                        {options.map(option => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        name={field}
                        value={editedRecord[field] || value || ""}
                        onChange={onChange}
                        variant="outlined"
                        size="small"
                        style={{ marginLeft: "10px" }}
                    />
                )
            ) : (
                <>
                    {value || ""}
                    {isEditable && (
                        <EditIcon
                            style={{ cursor: "pointer", marginLeft: "10px" }}
                            onClick={() => onEdit(field)}
                        />
                    )}
                </>
            )}
        </div>
    );

    const hasEditableFields = Object.values(editState).some(state => state);

    return (
        <Box sx={{ textAlign: "left", fontSize: "5.2mm", marginLeft: "20px" }}>
            {renderField("Name", "name", fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"])}
            {renderField("Mobile No", "mobileNo", fetchedRecord["Mobile No."], false)}
            {renderField("FOLK Level", "folkLevel", fetchedRecord["FOLK Level (Display)"], true, true, folkLevelOptions)}
            {fetchedRecord["FOLK Level (Display)"] && (
                <div style={{ marginBottom: "4mm" }}>
                    <span style={{ fontWeight: "500", marginRight: "4px" }}>Location:</span>
                    {getFolkLevelLocation(fetchedRecord["FOLK Level (Display)"])}
                </div>
            )}
            {/* Conditional fields for GFY Registered */}
            {(editedRecord.folkLevel === "GFY Registered" || fetchedRecord["FOLK Level (Display)"] === "GFY Registered") && (
                <>
                    <div style={{ marginBottom: "4mm" }}>
                        <span style={{ fontWeight: "500", marginRight: "4px" }}>Payment Screenshot:</span>
                        <div style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
                            {fetchedRecord["GFY Payment Screenshot"] && fetchedRecord["GFY Payment Screenshot"][0] ? (
                                <div style={{ marginRight: "10px" }}>
                                    <a 
                                        href={fetchedRecord["GFY Payment Screenshot"][0]?.url || fetchedRecord["GFY Payment Screenshot"][0]?.thumbnails?.large?.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: "block", textDecoration: "none" }}
                                    >
                                        <img
                                            src={fetchedRecord["GFY Payment Screenshot"][0]?.url || fetchedRecord["GFY Payment Screenshot"][0]?.thumbnails?.large?.url}
                                            alt="Payment Screenshot"
                                            style={{ 
                                                maxWidth: "150px", 
                                                maxHeight: "150px", 
                                                objectFit: "contain", 
                                                border: "1px solid #ccc", 
                                                cursor: "pointer",
                                                display: "block"
                                            }}
                                        />
                                    </a>
                                    <div>Uploaded (tap to view full size)</div>
                                </div>
                            ) : (
                                <span style={{ marginRight: "10px" }}>Not uploaded</span>
                            )}
                            {editState.paymentScreenshot ? (
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        console.log('File selected:', file);
                                        if (file) {
                                            const reader = new FileReader();
                                            reader.onload = (event) => {
                                                const base64 = event.target.result;
                                                console.log('File converted to base64, length:', base64.length);
                                                onChange({ 
                                                    target: { 
                                                        name: 'paymentScreenshot', 
                                                        value: {
                                                            filename: file.name,
                                                            type: file.type,
                                                            size: file.size,
                                                            base64: base64
                                                        }
                                                    } 
                                                });
                                            };
                                            reader.readAsDataURL(file);
                                        }
                                    }}
                                    style={{ marginLeft: "10px" }}
                                />
                            ) : (
                                <EditIcon
                                    style={{ cursor: "pointer", marginLeft: "10px" }}
                                    onClick={() => onEdit("paymentScreenshot")}
                                />
                            )}
                        </div>
                    </div>
                    {renderField("Payment Amount", "paymentAmount", fetchedRecord["GFY Payment"])}
                </>
            )}
            
            {renderField("Ashraya Level", "ashrayaLevel", fetchedRecord["Current Ashraya Level"], true, true, ashrayaLevelOptions)}
            {renderField("Course Material", "courseMaterial", fetchedRecord["Course Material"], true, true, courseMaterialOptions)}
            {renderField("FOLK Guide", "folkGuide", fetchedRecord["Guide Name"], false)}
            {renderField("Occupation", "occupation", fetchedRecord["Occupation"], true, true, occupationOptions)}
            {renderField("FOLK Centre", "folkCentre", fetchedRecord["FOLK Centre"], true, true, folkCentreOptions)}
            {renderField("Area of Stay", "areaOfStay", fetchedRecord["Area of Stay"])}
            
            <div style={{ marginBottom: "4mm" }}>
                <span style={{ fontWeight: "500", marginRight: "4px" }}>Chanting Rounds:</span>
                {editState.chantingRounds ? (
                    <Select
                        name="chantingRounds"
                        value={editedRecord.chantingRounds ?? fetchedRecord["Chanting"] ?? ""}
                        onChange={onChange}
                        variant="outlined"
                        size="small"
                        style={{ marginLeft: "10px" }}
                    >
                        {Array.from({ length: 17 }, (_, i) => (
                            <MenuItem key={i} value={i}>{i}</MenuItem>
                        ))}
                    </Select>
                ) : (
                    <>
                        {fetchedRecord["Chanting"]?.toString() || ""}
                        <EditIcon
                            style={{ cursor: "pointer", marginLeft: "10px" }}
                            onClick={() => onEdit("chantingRounds")}
                        />
                    </>
                )}
            </div>

            {/* {renderField("Club", "club", fetchedRecord["FOLK Recent Club"], false)}
            {fetchedRecord["FOLK Recent Club"] && (
                <div style={{ marginBottom: "4mm" }}>
                    <span style={{ fontWeight: "500", marginRight: "4px" }}>Club Location:</span>
                    {getClubLocation(fetchedRecord["FOLK Recent Club"])}
                </div>
            )} */}
            {renderField("Count", "count", fetchedRecord["Count"], false)}

            <div style={{ marginTop: "4mm", display: "flex", alignItems: "center" }}>
                <span style={{ fontWeight: "500", marginRight: "4px" }}>Photo:</span>
                <div style={{
                    width: "200px", height: "200px", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    borderRadius: "8px", overflow: "hidden"
                }}>
                    <img
                        src={fetchedRecord["Photo"] && fetchedRecord["Photo"][0]?.thumbnails?.large?.url}
                        alt="Photo"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                </div>
            </div>

            <br />
            <div style={{ marginBottom: "5mm" }}>
                <span style={{ fontWeight: "500", marginRight: "4px" }}>Date:</span> {getDateStr()}
            </div>
            <div style={{ marginBottom: "4mm" }}>
                <span style={{ fontWeight: "500", marginRight: "4px" }}>Time:</span> {getTimeStr()}
            </div>

            {hasEditableFields && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                    <Button
                        variant="outlined"
                        onClick={onUpdate}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size="1.5rem" color="inherit" /> : "Update"}
                    </Button>
                </div>
            )}
        </Box>
    );
}